/**
 * Internal types shared across poolMembership/* components. These are
 * editor-local helpers layered on top of the protocol types from
 * `@interlucent/organization-protocol`; the protocol types are the
 * authoritative wire contract.
 */

import type {
  CandidateAppNode,
  CandidateOrgNode,
  CandidateProjectNode,
  CandidateVersionNode,
  CreateInclusionInput,
  CreateRefinementInput,
  GetCandidatesResponse,
  InclusionNodeKind,
  PoolInclusion,
  PoolRefinement,
  RefinementNodeKind,
} from '@interlucent/organization-protocol';

/**
 * In-memory index for `(nodeKind, nodeId) → display label + ancestors`.
 * Built once from GetCandidatesResponse so InclusionCard doesn't need to
 * walk the whole tree to render a node name. Ancestor ids let us decide
 * whether a refinement sits inside a given inclusion's subtree.
 */
export interface CandidateIndexEntry {
  kind: InclusionNodeKind;
  id: string;
  /** Short human label (e.g. "Acme · Foo · Bar" or the version id). */
  label: string;
  /** Ancestor ids at each level; undefined where the kind doesn't apply. */
  orgId?: string;
  projectId?: string;
  appId?: string;
}

export interface CandidateIndex {
  /** `${kind}:${id}` → entry. */
  byKey: Map<string, CandidateIndexEntry>;
  /** Total counts by kind across the tenancy-filtered candidate tree. */
  counts: {
    orgs: number;
    projects: number;
    apps: number;
    versions: number;
  };
}

/**
 * Build a lookup index from the candidate tree. The index is used by cards
 * to display node names for inclusions whose target is deeper than the
 * card's own kind (e.g. an org inclusion referencing a project id through
 * an exception chip).
 */
export function buildCandidateIndex(tree: GetCandidatesResponse): CandidateIndex {
  const byKey = new Map<string, CandidateIndexEntry>();
  let orgs = 0;
  let projects = 0;
  let apps = 0;
  let versions = 0;

  for (const org of tree.orgs) {
    orgs++;
    indexOrg(org, byKey);
    for (const project of org.projects) {
      projects++;
      indexProject(project, org, byKey);
      for (const app of project.apps) {
        apps++;
        indexApp(app, project, org, byKey);
        for (const v of app.versions) {
          versions++;
          indexVersion(v, app, project, org, byKey);
        }
      }
    }
  }

  return { byKey, counts: { orgs, projects, apps, versions } };
}

function indexOrg(org: CandidateOrgNode, byKey: Map<string, CandidateIndexEntry>) {
  byKey.set(`org:${org.id}`, {
    kind: 'org',
    id: org.id,
    label: org.name || org.id,
    orgId: org.id,
  });
}

function indexProject(p: CandidateProjectNode, org: CandidateOrgNode, byKey: Map<string, CandidateIndexEntry>) {
  byKey.set(`project:${p.id}`, {
    kind: 'project',
    id: p.id,
    label: `${org.name} · ${p.name}`,
    orgId: org.id,
    projectId: p.id,
  });
}

function indexApp(a: CandidateAppNode, p: CandidateProjectNode, org: CandidateOrgNode, byKey: Map<string, CandidateIndexEntry>) {
  byKey.set(`app:${a.id}`, {
    kind: 'app',
    id: a.id,
    label: `${p.name} · ${a.name || a.slug}`,
    orgId: org.id,
    projectId: p.id,
    appId: a.id,
  });
}

function indexVersion(
  v: CandidateVersionNode,
  a: CandidateAppNode,
  p: CandidateProjectNode,
  org: CandidateOrgNode,
  byKey: Map<string, CandidateIndexEntry>,
) {
  byKey.set(`version:${v.id}`, {
    kind: 'version',
    id: v.id,
    label: `${a.name || a.slug} · ${v.id.slice(0, 10)}`,
    orgId: org.id,
    projectId: p.id,
    appId: a.id,
  });
}

/**
 * True when the refinement's node sits inside the inclusion's subtree
 * according to the candidate index. `version` refinements live under
 * `app`/`project`/`org` inclusions; `app` refinements live under
 * `project`/`org`; `project` refinements live under `org` or the pool's
 * own project (for non-multi tenancy).
 */
export function refinementInInclusionSubtree(
  refinement: { nodeKind: RefinementNodeKind; nodeId: string },
  inclusion: { nodeKind: InclusionNodeKind; nodeId: string },
  index: CandidateIndex,
): boolean {
  // An inclusion always covers its own node.
  if (refinement.nodeKind === inclusion.nodeKind && refinement.nodeId === inclusion.nodeId) {
    return true;
  }
  const refEntry = index.byKey.get(`${refinement.nodeKind}:${refinement.nodeId}`);
  if (!refEntry) return false;
  if (inclusion.nodeKind === 'org') return refEntry.orgId === inclusion.nodeId;
  if (inclusion.nodeKind === 'project') return refEntry.projectId === inclusion.nodeId;
  if (inclusion.nodeKind === 'app') return refEntry.appId === inclusion.nodeId;
  // A version inclusion's subtree is just the version itself (handled above).
  return false;
}

/**
 * Client-side placeholder for a `PendingOp { kind: 'addInclusion' }` so the
 * UI can render a card for it before Save lands. The `id` is a local
 * `staged-<n>` string so lookups stay distinct from server `PoolInclusion`
 * ids, and the update/remove ops for it can be keyed off `stagedIndex`.
 */
export interface StagedInclusion {
  id: string;                 // local-only id: "staged-<opIndex>"
  stagedIndex: number;        // index into pendingOps where the addInclusion sits
  poolId: string;
  nodeKind: InclusionNodeKind;
  nodeId: string;
  onlyDefaultVersion: boolean;
  precacheOverride: boolean | null;
  /** @deprecated Use warmTargetOverride. Dropped in PR 5. */
  prelaunchOverride: boolean | null;
  warmTargetOverride: number | null;
  multiplexOverride: boolean | null;
}

export function makeStagedInclusion(
  poolId: string,
  payload: CreateInclusionInput,
  stagedIndex: number,
): StagedInclusion {
  return {
    id: `staged-${stagedIndex}`,
    stagedIndex,
    poolId,
    nodeKind: payload.nodeKind,
    nodeId: payload.nodeId,
    onlyDefaultVersion: payload.onlyDefaultVersion ?? false,
    precacheOverride: payload.precacheOverride ?? null,
    prelaunchOverride: payload.prelaunchOverride ?? null,
    warmTargetOverride: payload.warmTargetOverride ?? null,
    multiplexOverride: payload.multiplexOverride ?? null,
  };
}

export interface StagedRefinement {
  id: string;                 // local-only id: "staged-<opIndex>"
  stagedIndex: number;
  poolId: string;
  nodeKind: RefinementNodeKind;
  nodeId: string;
  action: CreateRefinementInput['action'];
  precacheOverride: boolean | null;
  /** @deprecated Use warmTargetOverride. Dropped in PR 5. */
  prelaunchOverride: boolean | null;
  warmTargetOverride: number | null;
  multiplexOverride: boolean | null;
}

export function makeStagedRefinement(
  poolId: string,
  payload: CreateRefinementInput,
  stagedIndex: number,
): StagedRefinement {
  return {
    id: `staged-${stagedIndex}`,
    stagedIndex,
    poolId,
    nodeKind: payload.nodeKind,
    nodeId: payload.nodeId,
    action: payload.action,
    precacheOverride: payload.precacheOverride ?? null,
    prelaunchOverride: payload.prelaunchOverride ?? null,
    warmTargetOverride: payload.warmTargetOverride ?? null,
    multiplexOverride: payload.multiplexOverride ?? null,
  };
}

/** Shared display wrapper for both persisted and staged inclusions. */
export type DisplayInclusion =
  | { kind: 'persisted'; inclusion: PoolInclusion; pendingUpdate?: Partial<PoolInclusion>; pendingRemoval?: boolean }
  | { kind: 'staged'; staged: StagedInclusion };

/** Shared display wrapper for both persisted and staged refinements. */
export type DisplayRefinement =
  | { kind: 'persisted'; refinement: PoolRefinement; pendingUpdate?: Partial<PoolRefinement>; pendingRemoval?: boolean }
  | { kind: 'staged'; staged: StagedRefinement };
